import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useEvent } from 'react-use';
import LoadingOutlined from '@ant-design/icons/lib/icons/LoadingOutlined';

import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { opTime } from 'utils/dates';
import { OpInfo } from 'new-components/DLS/OpInfo/OpInfo';
import { OpSpin } from 'new-components/DLS/OpSpin/OpSpin';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive } from 'utils/helpers';

import STATUS from '../../../../../constants/status';
import idBarcode from '../../../../../images/idBack.png';
import { setSelectedVisit } from '../../../../../store/slices/visitsSlice';
import { AppDispatch } from '../../../../../store/store';
import { VisitorSearch } from '../../../../../types/visitorSearchTypes';
import { Visit } from '../../../../../types/visitTypes';
import { formatFullName } from '../../../../../utils/utils';

interface ScanIdComponentProps {
  open: boolean;
  visitorsToday: VisitorSearch[];
  onClose: () => void;
  openVisitorsDrawer: () => void;
  openSelectVisitorsModal: () => void;
  openSignInManuallyDrawer: () => void;
  setVisitorNameFilter: (
    filter:
      | { firstName: string; middleName: string; lastName: string }
      | undefined,
  ) => void;
  setNewVisitorName: (
    filter:
      | {
          firstName: string;
          middleName: string;
          lastName: string;
          dob?: string;
        }
      | undefined,
  ) => void;
}

interface OptionType {
  value: string;
  label: string;
  key: string;
}

interface DescribeVisitResponse {
  data?: Visit[] | null;
}

const shiftedDigitSymbolMap: Record<string, string> = {
  '0': ')',
  '1': '!',
  '2': '@',
  '3': '#',
  '4': '$',
  '5': '%',
  '6': '^',
  '7': '&',
  '8': '*',
  '9': '(',
};

const getAsciiCharFromKeyboardEvent = (event: KeyboardEvent) => {
  if (event.key.length === 1 && /^[\x20-\x7E]$/.test(event.key)) {
    return event.key;
  }

  if (/^Key[A-Z]$/.test(event.code)) {
    const letter = event.code.slice(3);
    return event.shiftKey ? letter : letter.toLowerCase();
  }

  if (/^Digit[0-9]$/.test(event.code)) {
    const digit = event.code.slice(5);
    return event.shiftKey ? shiftedDigitSymbolMap[digit] || digit : digit;
  }

  if (event.code === 'Space') {
    return ' ';
  }

  return '';
};

const ScanIDModal = ({
  open,
  visitorsToday,
  onClose,
  openVisitorsDrawer,
  openSelectVisitorsModal,
  openSignInManuallyDrawer,
  setVisitorNameFilter,
  setNewVisitorName,
}: ScanIdComponentProps) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();
  const { userId: globalUserId } = useIdentityAndUser();
  const { isValid: hasAllVisitorsWrite } = useValidateScopes([
    'o{orgId}-visitorAllVisitors:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);

  const scannedDataRef = useRef('');
  const [scanningInProgress, setScanningInProgress] = useState(false);
  const [isKeyPressed, setIsKeyPressed] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null); // Timer to detect the end of scanning

  const resetScanState = useCallback(
    ({
      preserveScannedData = false,
    }: { preserveScannedData?: boolean } = {}) => {
      setScanningInProgress(false);
      setIsKeyPressed(false);
      if (!preserveScannedData) {
        scannedDataRef.current = '';
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    },
    [],
  );

  useEvent(
    'keydown',
    scanningInProgress
      ? (event: Event) => handleKeyDown(event as KeyboardEvent)
      : null,
    document,
    { capture: true },
  );

  useEffect(() => {
    if (open) {
      setScanningInProgress(true);
    } else {
      resetScanState();
    }
  }, [open, resetScanState]);

  const parseDriverLicense = useCallback((data: string) => {
    const elementIds = [
      'DCA',
      'DCB',
      'DCD',
      'DBA',
      'DCS',
      'DAC',
      'DAD',
      'DBD',
      'DBB',
      'DBC',
      'DAY',
      'DAU',
      'DAG',
      'DAI',
      'DAJ',
      'DAK',
      'DAQ',
      'DCF',
      'DCG',
      'DDE',
      'DDF',
      'DDG',
      'DCT',
    ];

    const extractField = (sourceData: string, field: string) => {
      const regex = new RegExp(
        `${field}(.*?)(?=${elementIds.map((id) => `(?:${id})`).join('|')}|$)`,
      );
      const match = sourceData.match(regex);
      return match ? match[1]?.trim() : 'NONE';
    };

    const lastName = extractField(data, 'DCS');
    const firstName =
      extractField(data, 'DAC') !== 'NONE'
        ? extractField(data, 'DAC')
        : extractField(data, 'DCT');
    const middleName = extractField(data, 'DAD');
    const dobRaw = extractField(data, 'DBB');
    const dob =
      dobRaw !== 'NONE'
        ? opTime(dobRaw, 'MMDDYYYY').format('YYYY-MM-DD')
        : 'NONE';

    return {
      lastName,
      firstName,
      middleName,
      dob,
    };
  }, []);

  const handleVisitorOptions = useCallback(
    async (parsedNames: {
      firstName: string;
      middleName: string;
      lastName: string;
    }) => {
      const filteredVisits = visitorsToday.filter(
        (visitor) =>
          opTime(visitor.timestamp).isSame(opTime(), 'day') &&
          visitor.status === STATUS.PENDING.id,
      );

      const newOptions: OptionType[] = filteredVisits
        .filter(
          (visitor) =>
            hasAllVisitorsWrite || visitor.hostUserId === globalUserId,
        )
        .filter((visitor) => {
          const firstNameMatch =
            parsedNames.firstName === 'NONE' ||
            visitor.firstName?.toLowerCase() ===
              parsedNames.firstName.toLowerCase();
          const lastNameMatch =
            parsedNames.lastName === 'NONE' ||
            visitor.lastName?.toLowerCase() ===
              parsedNames.lastName.toLowerCase();
          return firstNameMatch && lastNameMatch;
        })
        .map((visitor) => ({
          value: `${visitor.visitId}-${visitor.id}`,
          label: formatFullName(
            visitor.firstName,
            visitor.middleName,
            visitor.lastName,
          ),
          key: `${visitor.visitId}-${visitor.id}`,
        }));
      if (newOptions.length === 0) {
        notificationService.show(
          'warning',
          t('scanIDModal.notification.noMatch.description'),
          t('scanIDModal.notification.noMatch.title'),
        );
        setNewVisitorName(parsedNames);
        openSignInManuallyDrawer();
      } else if (newOptions.length === 1) {
        const [visitId] = newOptions[0].value.split('-');
        const selectedVisit = (await heliumReceive('describeVisit', [
          orgId,
          visitId,
        ])) as unknown as DescribeVisitResponse;
        if (selectedVisit.data?.[0]) {
          dispatch(setSelectedVisit(selectedVisit.data[0]));
        }
        openVisitorsDrawer();
      } else if (newOptions.length > 1) {
        openSelectVisitorsModal();
      }
    },
    [
      orgId,
      visitorsToday,
      t,
      dispatch,
      openVisitorsDrawer,
      openSelectVisitorsModal,
      openSignInManuallyDrawer,
      setNewVisitorName,
      hasAllVisitorsWrite,
      globalUserId,
    ],
  );

  const handleScannedData = useCallback(
    (data: string): void => {
      if (data.startsWith('@ANSI') || data.startsWith('@AAMVA')) {
        const cleanedData = data.replace(/Shift/g, '');
        const extractedData = parseDriverLicense(cleanedData);
        if (
          extractedData.lastName !== 'NONE' ||
          extractedData.firstName !== 'NONE' ||
          extractedData.middleName !== 'NONE'
        ) {
          setVisitorNameFilter(extractedData);
          handleVisitorOptions(extractedData);
        } else {
          notificationService.show(
            'error',
            t('scanIDModal.notification.scanError.description'),
            t('scanIDModal.notification.scanError.title'),
          );
        }
      } else {
        notificationService.show(
          'error',
          t('scanIDModal.notification.invalidBarcode.description'),
          t('scanIDModal.notification.invalidBarcode.title'),
        );
      }
    },
    [t, parseDriverLicense, handleVisitorOptions, setVisitorNameFilter],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.isComposing) {
        return;
      }

      if (event.key === 'Enter' || event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }

      setIsKeyPressed(true);

      const asciiChar = getAsciiCharFromKeyboardEvent(event);
      if (asciiChar) {
        scannedDataRef.current += asciiChar;
      }

      // End scan if no additional input within 1 second
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        const scannedData = scannedDataRef.current;
        resetScanState({ preserveScannedData: true });
        handleScannedData(scannedData);
        onClose();
      }, 1000);
    },
    [onClose, handleScannedData, resetScanState],
  );

  return (
    <OpModal
      title={t('scanIDModal.title')}
      open={open}
      closable={!isKeyPressed}
      keyboard={false}
      mask={{ closable: false }}
      onCancel={(event) => {
        const cancelEvent = event as {
          type?: string;
          key?: string;
          preventDefault?: () => void;
          stopPropagation?: () => void;
        };
        if (isKeyPressed) {
          cancelEvent.preventDefault?.();
          cancelEvent.stopPropagation?.();
          return;
        }

        resetScanState();
        onClose();
      }}
      okText={t('scanIDModal.confirm')}
      cancelText={t('scanIDModal.cancel')}
      centered
      style={{ textAlign: 'center' }}
      footer={null}
    >
      <p>
        <img src={idBarcode} alt="Barcode ID" style={{ width: '250px' }} />
      </p>
      {scanningInProgress ? (
        <OpSpin
          indicator={<LoadingOutlined spin />}
          size="default"
          style={{ padding: '20px' }}
        />
      ) : undefined}
      {isKeyPressed ? (
        <OpInfo title={t('scanIDModal.alert.processing')} type="success" />
      ) : (
        <OpInfo title={t('scanIDModal.alert.waiting')} type="info" />
      )}
    </OpModal>
  );
};

export default ScanIDModal;
